import dayjs from "dayjs";
import numbro from "numbro";
import type {
	DocumentTuple,
	FundSize,
	NumberOfHoldings,
	RawValue,
} from "../api.types";
import { currencyCodes as currencies } from "../constants";
import type { CurrencyFormat, Formatter } from "../types";

const SerpentineFormatRegex = /{(.?:)?([^{}]*)}/g;
const OperationRegex = /\(([x*/])(\d+)\)$/;
const NumberRegex = /[,0#]*\.[0#]*/g;
const OperationMap = {
	x: (a: number, b: number) => a * b,
	"*": (a: number, b: number) => a * b,
	"/": (a: number, b: number) => a / b,
};

export type Operation = keyof typeof OperationMap;

export const formatter: Formatter = (meta, rawObj, rawValue) => {
	// if (!(Array.isArray(rawValue) ? rawValue.length : rawValue)) {
	//   return meta.null_replacement;
	// }
	switch (meta.type) {
		case "string":
			return formatString(meta, rawObj, rawValue as string);
		case "date":
			return formatDate(meta, rawObj, rawValue as string);
		case "number":
			return formatNumber(meta, rawObj, rawValue as string);
		case "currency":
			return formatCurrency(meta, rawObj, rawValue);
		case "document":
			return formatDocument(meta, rawObj, rawValue);
		case "dict":
			return formatDict(meta, rawObj, rawValue);
		default:
			return JSON.stringify(rawValue);
	}
};

const isFundSize = (value: RawValue): value is FundSize => {
	if (!value) {
		return false;
	}
	return typeof value === "object" && currencies.some((cur) => cur in value);
};
const isNumberOfHoldings = (value: RawValue): value is NumberOfHoldings => {
	if (!value) {
		return false;
	}
	return typeof value === "object" && "NumberOfPositions" in value;
};
const isNumberOfFunds = (value: RawValue): value is NumberOfHoldings => {
	if (!value) {
		return false;
	}
	return typeof value === "object" && "Fund" in value;
};
const isString = (value: RawValue): value is string => {
	return typeof value === "string";
};
const isDocument = (value: RawValue): value is DocumentTuple[] => {
	return (
		Array.isArray(value) &&
		value.length > 0 &&
		Array.isArray(value[0]) &&
		value[0].every((v) => isString(v))
	);
};

const formatDict: Formatter<Record<string, string> | null> = (
	meta,
	rawObj,
	rawValue,
) => {
	if (isFundSize(rawValue)) {
		return (Object.entries(rawValue) as [keyof FundSize, string][]).reduce(
			(acc, [cur, val]) => {
				acc[cur] = formatCurrency({ ...meta, currency: cur }, rawObj, val);
				return acc;
			},
			{} as FundSize,
		);
	}
	if (isNumberOfHoldings(rawValue)) {
		return {
			NumberOfPositions: formatNumber(
				meta,
				rawObj,
				`${rawValue.NumberOfPositions}`,
			),
		};
	}
	if (isNumberOfFunds(rawValue)) {
		return {
			NumberOfPositions: formatNumber(meta, rawObj, `${rawValue.Fund}`),
		};
	}
	return null;
};

const formatString: Formatter<string> = (
	{ format, null_replacement },
	_,
	rawValue,
) => {
	const isStringArray = Array.isArray(rawValue) && rawValue.every(isString);
	if (!isString(rawValue) && !isStringArray) {
		return null_replacement;
	}
	if (isStringArray) {
		return rawValue
			.map((v) => format?.replace(SerpentineFormatRegex, v) ?? v)
			.join(", ");
	}
	return format?.replace(SerpentineFormatRegex, () => rawValue) ?? rawValue;
};

const formatDate: Formatter<string> = (
	{ format, null_replacement },
	_,
	rawValue,
) => {
	if (!isString(rawValue)) {
		return null_replacement;
	}
	const defaultFormat = "YYYY-MM-DD";
	const parseFormat = defaultFormat;
	const parsedDate = dayjs(rawValue, parseFormat);

	if (!parsedDate.isValid()) {
		return rawValue;
	}
	if (!format) {
		return parsedDate.format(defaultFormat);
	}

	return format.replace(
		SerpentineFormatRegex,
		(fullMatch, firstGroup, secondGroup) => {
			return parsedDate.format(secondGroup || defaultFormat);
		},
	);
};

function getPattern(formatString: string) {
	const match = formatString.match(NumberRegex);
	if (!match) {
		throw new Error(`Invalid number pattern ${formatString}`);
	}
	return match[0];
}

function formatNumbroWithLDML(
	numbroNumber: numbro.Numbro,
	formatString: string,
) {
	if (formatString.trim().length === 0) {
		return numbroNumber.format();
	}
	const semicolon = formatString.indexOf(";");
	let formattedNumber = "";
	if (semicolon > -1) {
		const posFormatString = formatString.slice(0, semicolon);
		const posPattern = getPattern(posFormatString);
		const negFormatString = formatString.slice(
			semicolon + 1,
			formatString.length,
		);
		const negPattern = getPattern(negFormatString);
		formattedNumber = numbroNumber.format(posPattern);

		if (numbroNumber.value() >= 0) {
			return posFormatString.replace(posPattern, formattedNumber);
		}
		// formattedNumber needs to be stripped of its - sign
		formattedNumber = formattedNumber.slice(1, formattedNumber.length);
		return negFormatString.replace(negPattern, formattedNumber);
	}

	const pattern = getPattern(formatString);
	formattedNumber = numbroNumber.format(pattern);

	return formatString.replace(pattern, formattedNumber);
}

function getParsedNumber(num: string) {
	// Special case
	const allowedStrings = ["N/A"];
	if (allowedStrings.indexOf(num.toUpperCase()) !== -1) {
		return "n/a";
	}

	// Validation
	const parsedNumber = Number.parseFloat(num);
	if (Number.isNaN(parsedNumber)) return "";

	return parsedNumber;
}

const formatNumber: Formatter<string> = (
	{ format },
	rawObj,
	rawVal,
): string => {
	if (!isString(rawVal) && typeof rawVal !== "number") {
		return "";
	}
	let parsedNumber = getParsedNumber(`${rawVal}`);
	if (!format || typeof parsedNumber !== "number") return `${rawVal}`;

	// Find operation strings in ret and apply them to the num
	const operationFormatMatch = format.match(OperationRegex);
	if (operationFormatMatch) {
		const operation = OperationMap[operationFormatMatch[1] as Operation];
		const numberMatch = Number.parseFloat(operationFormatMatch[2] as string);
		parsedNumber = operation(parsedNumber, numberMatch);
	}
	const numbroNumber = numbro(parsedNumber);

	// Remove any operationFormatMatch from formatString and replace numeralFormatMatches with the formatted number
	return format
		.split(OperationRegex)[0]
		?.replace(SerpentineFormatRegex, (fullMatch, firstGroup, secondGroup) => {
			return formatNumbroWithLDML(numbroNumber, secondGroup);
		});
};

const formatCurrency: Formatter<string> = (meta, rawObj, rawVal) => {
	if (!isString(rawVal)) {
		return "";
	}
	const parsedNumber = getParsedNumber(rawVal);
	const { format_rules: formatRules, format: defaultFormatString } = meta;
	const currency = meta.currency;

	if (typeof parsedNumber !== "number") {
		return rawVal;
	}
	if (!currency || !formatRules || !Object.keys(formatRules).length) {
		return formatNumber(meta, rawObj, `${parsedNumber}`);
	}

	const format = formatRules
		.filter(
			(rule) =>
				Object.keys(rule.formats as CurrencyFormat).indexOf(currency) > -1,
		)
		.filter((rule) => parsedNumber > Number.parseFloat(`${rule.min_value}`))
		.sort(
			(a, b) =>
				Number.parseFloat(`${b.min_value}`) -
				Number.parseFloat(`${a.min_value}`),
		)[0];

	return format && currency
		? formatNumber(
				{
					...meta,
					format: (format.formats as CurrencyFormat)[currency] ?? null,
				},
				rawObj,
				`${parsedNumber}`,
			)
		: formatNumber(
				{ ...meta, format: defaultFormatString },
				rawObj,
				`${parsedNumber}`,
			);
};

const formatDocument: Formatter<Record<string, string>[]> = (
	_,
	__,
	rawValue,
) => {
	if (!isDocument(rawValue)) {
		return [];
	}
	return rawValue.map(([url, name, language]) => ({
		url,
		name,
		language,
		asAtDate: "",
	}));
};

export const abbrNumber = (numStr: string) => {
	const num = getParsedNumber(numStr);
	if (typeof num !== "number") {
		return num;
	}
	const absNum = Math.abs(num);
	if (absNum >= 1e9) {
		return `${(num / 1e9).toFixed(2)}B`;
	}
	if (absNum >= 1e6) {
		return `${(num / 1e6).toFixed(2)}M`;
	}
	if (absNum >= 1e3) {
		return `${(num / 1e3).toFixed(2)}K`;
	}
	return num.toString();
};

export const parseFloatIntl = (x: string) =>
	Number.parseFloat(/,/.test(x) ? x.replace(",", ".") : x);
